import React, { useMemo } from 'react';
import { Box, Flex, TextV2 } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import { useTranslation } from '@shared/core';
import { Check, JoyAiV2 } from '@withjoy/joykit/icons';
import { AccommodationPropertyWithCalculatedPrice } from '@shared/hooks/useSearchExtendedAccommodationProperties';
import { TextEllipsisWrapper } from '@shared/utils/formatting/textEllipsis';
import { StarRating } from '@shared/components';
import { DistanceFromVenue } from '@shared/components/DistanceFromVenue';
import { DistanceStatistics } from '@shared/components/DistanceStatistics';
import { HotelPhotoCarousel } from '@shared/components/HotelPhotoCarousel';
import { DEFAULT_HOTEL_IMAGE } from '@shared/utils/constants';
import { ReactComponent as JoyLogo } from '@assets/joy-logo.svg';
import { useIsMobileOrTablet } from '@shared/utils/media/useMediaScreens';
import { HotelSkeleton } from './HotelSkeleton';

interface HotelsProps
  extends Readonly<{
    isLoading: boolean;
    hotels: ReadonlyArray<AccommodationPropertyWithCalculatedPrice>;
    allHotelCount: number;
    goodMatchedHotelsCount: number;
    bestHotelsCount: number;
    children?: React.ReactNode;
  }> {}

export const Hotels = ({ isLoading, hotels, allHotelCount, goodMatchedHotelsCount, bestHotelsCount, children }: HotelsProps) => {
  const { t } = useTranslation('websiteEditor');
  const hotelsTrans = t('venueHotels');
  return (
    <Flex
      flex={1}
      flexDirection="column"
      gap={7}
      alignItems="center"
      justifyContent="center"
      width={'100%'}
      paddingX={{ _: 0, md: 9 }}
      paddingTop={{ __: 6, sm2: 0 }}
      paddingBottom={{ __: pxToRem(200), sm2: pxToRem(180) }}
    >
      <Flex
        gap={3}
        alignItems="center"
        paddingX={{ __: 6, xs: 7, md: pxToRem(62) }}
      >
        <JoyAiV2 size={48} />
        <Box
          typographyVariant="hed2"
          textAlign="left"
          maxWidth={{ _: '100%', md: pxToRem(456) }}
        >
          {isLoading ? hotelsTrans.loadingTitle() : hotelsTrans.title({ allHotelCount, goodMatchedHotelsCount, bestHotelsCount })}
        </Box>
      </Flex>
      <Flex
        flexDirection="column"
        gap={5}
        alignItems="flex-start"
        justifyContent="center"
        paddingX={{ _: 6, md: 0 }}
        width={'100%'}
        maxWidth={pxToRem(640)}
        minHeight={{ __: pxToRem(347), md: pxToRem(209) }}
      >
        {isLoading ? (
          <HotelSkeleton id={`1`} />
        ) : (
          <>
            {hotels.map((hotel, idx) => (
              <HotelTile
                key={`${hotel.joyPlaceId}`}
                id={`${idx + 1}`}
                hotel={hotel}
              />
            ))}
          </>
        )}
      </Flex>
      {children}
    </Flex>
  );
};

interface HotelTileProps
  extends Readonly<{
    id: string;
    hotel: AccommodationPropertyWithCalculatedPrice;
  }> {}

const HotelTile = ({ id, hotel }: HotelTileProps) => {
  const { joyPlaceId, name, starRating, distance, photos, price, percentageOff, strikeOutPrice } = hotel;

  const { t } = useTranslation('websiteEditor');
  const hotelsTrans = t('venueHotels');
  const isMobileOrTablet = useIsMobileOrTablet();

  const images = useMemo(() => {
    const filteredImages = photos
      .filter((photo): photo is { thumbnailUrl: string } => typeof photo.thumbnailUrl === 'string' && photo.thumbnailUrl.trim() !== '')
      .map(photo => ({ src: photo.thumbnailUrl, alt: name || '' }));

    if (filteredImages.length === 0) {
      filteredImages.push({ src: DEFAULT_HOTEL_IMAGE, alt: name || '' });
    }
    return filteredImages;
  }, [name, photos]);

  return (
    <Flex
      alignItems={'center'}
      gap={5}
      width={'100%'}
      flexDirection={{ __: 'column', xs: 'row' }}
    >
      <TextV2
        display={'flex'}
        alignItems={'center'}
        justifyContent={'center'}
        typographyVariant={'hed1'}
        fontSize={pxToRem(16)}
        fontWeight={600}
        borderRadius={'50%'}
        border={'1px solid'}
        borderColor={'mono3'}
        width={pxToRem(40)}
        height={pxToRem(40)}
        textAlign={'center'}
        flex={'none'}
      >
        {id}
      </TextV2>
      <Flex
        gap={5}
        padding={5}
        border={'1px solid'}
        borderColor={'mono3'}
        borderRadius={pxToRem(16)}
        alignItems={'center'}
        flex={1}
        position={'relative'}
        flexDirection={{ __: 'column', md: 'row' }}
      >
        <Flex
          width={{ __: '100%', md: 'auto' }}
          alignItems={'center'}
          justifyContent={'center'}
          position={'relative'}
          backgroundColor={'mono3'}
          borderRadius={pxToRem(8)}
        >
          <HotelPhotoCarousel
            hotel={hotel}
            images={images}
            context="Accommodations.HotelTile"
            width={{ __: '100%', md: pxToRem(122) }}
            height={pxToRem(122)}
            aspectRatio={isMobileOrTablet ? '2/1' : '1/1'}
            navigation={{ variant: 'minimal', visible: 'default' }}
            borderRadius={pxToRem(8)}
            display={'flex'}
            flex={'none'}
          />
          <Flex
            position={'absolute'}
            left={'50%'}
            top={images.length > 1 ? 6 : '50%'}
            transform={'translate(-50%, -50%)'}
            gap={pxToRem(1.75)}
            paddingX={pxToRem(10.5)}
            height={pxToRem(24.5)}
            borderRadius={pxToRem(14)}
            border="0.875px solid rgba(255, 255, 255, 0.50)"
            background="rgba(255, 255, 255, 0.72)"
            alignItems={'center'}
            backdropFilter={`blur(21px)`}
          >
            <Check size={16} />
            <TextV2
              typographyVariant={'label1'}
              display={'flex'}
              alignItems={'center'}
              fontSize={pxToRem(11.375)}
              fontWeight={500}
            >
              {hotelsTrans.added()}
            </TextV2>
          </Flex>
        </Flex>
        <Flex
          flexDirection={'column'}
          width={'100%'}
        >
          <TextEllipsisWrapper
            lineClamp={2}
            typographyVariant={'hed1'}
          >
            {name}
          </TextEllipsisWrapper>
          {(starRating || distance) && (
            <Flex
              marginTop={1}
              gap={4}
              alignItems={'center'}
            >
              {starRating && (
                <StarRating
                  id={`${joyPlaceId}`}
                  rating={starRating}
                  showPartialRating
                  hideNonFilledStars
                  iconSize={16}
                />
              )}
              {distance && (
                <>
                  <DistanceFromVenue
                    milesToVenue={distance}
                    color="mono14"
                    useToWording
                  />
                  <DistanceStatistics
                    milesToVenue={distance}
                    color="mono14"
                    useShortWording
                  />
                </>
              )}
            </Flex>
          )}
          {price && (
            <Flex
              justifyContent={'space-between'}
              marginTop={2}
              width={'100%'}
            >
              <Flex
                flexDirection={'column'}
                gap={3}
                justifyContent={'space-between'}
                alignItems={'center'}
              >
                <TextV2
                  typographyVariant={'label2'}
                  fontSize={pxToRem(15)}
                  fontWeight={600}
                >
                  {hotelsTrans.guestSpecialRate()}
                </TextV2>
                <Flex
                  gap={pxToRem(7)}
                  alignItems={'center'}
                  width={'100%'}
                >
                  <JoyLogo
                    color="white"
                    style={{ backgroundColor: '#494DCB', width: pxToRem(24), height: pxToRem(24), borderRadius: '50%', padding: pxToRem(2) }}
                  />
                  <TextV2
                    typographyVariant={'label2'}
                    fontSize={pxToRem(15)}
                    color={'mono8'}
                    fontWeight={400}
                  >
                    {hotelsTrans.joyTravel()}
                  </TextV2>
                </Flex>
              </Flex>
              <Flex
                flexDirection={'column'}
                gap={1}
                flexWrap={'wrap-reverse'}
                justifyContent={'flex-end'}
              >
                {percentageOff && percentageOff > 0 && (
                  <TextV2
                    typographyVariant="label2"
                    fontWeight={500}
                    paddingX={3}
                    paddingY={1}
                    fontSize={pxToRem(13)}
                    lineHeight={'18px'}
                    borderRadius={200}
                    backgroundColor={'accentGreen2'}
                    color={'accentGreen3'}
                    width={'fit-content'}
                    whiteSpace={'nowrap'}
                  >
                    {hotelsTrans.savePercentageOff({ percentageOff })}
                  </TextV2>
                )}
                <Flex gap={3}>
                  {strikeOutPrice && (
                    <TextV2
                      typographyVariant={'hed2'}
                      fontWeight={400}
                      color={'mono8'}
                      textDecorationLine={'line-through'}
                    >
                      {strikeOutPrice}
                    </TextV2>
                  )}
                  <TextV2 typographyVariant={'hed2'}>{hotelsTrans.pricePerNight({ price })}</TextV2>
                </Flex>
              </Flex>
            </Flex>
          )}
        </Flex>
      </Flex>
    </Flex>
  );
};
